import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

export default function AuthCallbackPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const hasOAuthHash = window.location.hash.includes('access_token');
    const isRecovery = window.location.hash.includes('type=recovery');

    // Password recovery — wait for session then redirect
    if (isRecovery) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        (event, session) => {
          if (event === 'PASSWORD_RECOVERY' && session) {
            navigate('/reset-password', { replace: true });
          }
        }
      );
      // Fallback timeout
      const timeout = setTimeout(() => {
        navigate('/reset-password', { replace: true });
      }, 3000);
      return () => {
        subscription.unsubscribe();
        clearTimeout(timeout);
      };
    }

    if (hasOAuthHash) {
      const handleSession = async (session: any) => {
        const isRequestAccess = localStorage.getItem('qbn_request_access') === 'true';

        if (isRequestAccess) {
          // Check if profile already exists (returning user)
          const { data: existingProfile } = await supabase
            .from('profiles')
            .select('membership_status, onboarding_completed')
            .eq('user_id', session.user.id)
            .maybeSingle();

          if (existingProfile) {
            // Existing profile - route based on status
            if (existingProfile.membership_status === 'applied') {
              localStorage.removeItem('qbn_request_access');
              navigate('/applications', { replace: true });
            } else if (existingProfile.onboarding_completed) {
              localStorage.removeItem('qbn_request_access');
              navigate('/home', { replace: true });
            } else {
              // Profile exists but incomplete - go to onboarding
              navigate('/onboarding', { replace: true });
            }
          } else {
            // New user requesting access - go to onboarding (profile created there)
            navigate('/onboarding', { replace: true });
          }
          return;
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_completed, membership_status')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (profile?.membership_status === 'applied') {
          navigate('/applications', { replace: true });
        } else if (profile?.onboarding_completed) {
          navigate('/home', { replace: true });
        } else {
          navigate('/onboarding', { replace: true });
        }
      };

      // Wait for Supabase to process the hash and establish session
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          if (event === 'PASSWORD_RECOVERY' && session) {
            navigate('/reset-password', { replace: true });
            return;
          }
          if (event === 'SIGNED_IN' && session) {
            await handleSession(session);
          }
        }
      );

      // Fallback: if no event fires within 5s, check session directly
      const timeout = setTimeout(async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await handleSession(session);
        } else {
          navigate('/auth', { replace: true });
        }
      }, 5000);

      return () => {
        subscription.unsubscribe();
        clearTimeout(timeout);
      };
    } else {
      // No OAuth hash — just a regular visit to / or /auth/callback
      const checkSession = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          navigate('/home', { replace: true });
        } else {
          navigate('/auth', { replace: true });
        }
      };
      checkSession();
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}
